import type { TFunction } from "i18next";
import {
  AlertCircle,
  Calendar as CalendarStepIcon,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Loader2,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAppointmentCreation } from "../hooks/use-appointment-creation";
import { useCustomerStep } from "../hooks/use-customer-step";
import { useSchedulingStep } from "../hooks/use-scheduling-step";
import { useServicesStep } from "../hooks/use-services-step";
// Hooks
import type { CreateAppointmentStep } from "../hooks/use-step-navigation";
import { useStepNavigation } from "../hooks/use-step-navigation";
// Step components
import { MobileCustomerStep } from "./steps/customer-step/mobile-customer-step";
import { MobileSchedulingStep } from "./steps/scheduling-step/mobile-scheduling-step";
import { MobileServicesStep } from "./steps/services-step/mobile-services-step";

interface MobileCreateAppointmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  prefilledData?: {
    customerId?: string;
    workerId?: string;
    startTime?: Date;
    date?: string;
    time?: string;
    notes?: string;
  };
}

const getSteps = (
  t: TFunction
): Array<{
  id: CreateAppointmentStep;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}> => [
  { id: "customer", title: t("booking.steps.customer"), icon: User },
  { id: "services", title: t("booking.steps.services"), icon: CreditCard },
  { id: "scheduling", title: t("booking.steps.scheduling"), icon: CalendarStepIcon },
];

export function MobileCreateAppointment({
  open,
  onOpenChange,
  onSuccess,
  prefilledData,
}: MobileCreateAppointmentProps) {
  const { t } = useTranslation("appointments");
  const steps = getSteps(t);

  // Initialize step hooks
  const customerStep = useCustomerStep({
    prefilledCustomerId: prefilledData?.customerId,
    readonly: !!prefilledData?.customerId, // Make readonly when customer is pre-selected
  });

  const servicesStep = useServicesStep();

  const schedulingStep = useSchedulingStep({
    prefilledData,
  });

  const {
    handleCreateAppointment,
    loading: createLoading,
    error: createError,
  } = useAppointmentCreation({
    onSuccess,
    onClose: () => onOpenChange(false),
  });

  // Step navigation - start on services step if customer is pre-selected
  const stepNavigation = useStepNavigation({
    initialStep: prefilledData?.customerId ? "services" : "customer",
    canGoToCustomer: () => true,
    canGoToServices: () => customerStep.isValid(),
    canGoToScheduling: () => customerStep.isValid() && servicesStep.isValid(),
  });

  const {
    currentStep,
    nextStep,
    prevStep,
    isFirstStep,
    isLastStep,
    getStepIndex,
    isStepCompleted,
  } = stepNavigation;

  // Handle final submission
  const handleSubmit = async () => {
    if (!customerStep.isValid() || !servicesStep.isValid() || !schedulingStep.isValid()) {
      return;
    }

    try {
      const finalCustomerId = await customerStep.getFinalCustomerId();
      const { startTime, endTime } =
        schedulingStep.getStartEndTimes(servicesStep.totalDuration) || {};

      if (!startTime || !endTime) {
        throw new Error(t("createStep.invalidDateTime"));
      }

      await handleCreateAppointment({
        customerId: finalCustomerId,
        selectedServices: servicesStep.selectedServices,
        startTime,
        notes: schedulingStep.notes,
      });
    } catch (error) {
      console.error("Failed to create appointment:", error);
    }
  };

  const canSubmit = () => {
    return customerStep.isValid() && servicesStep.isValid() && schedulingStep.isValid() && !loading;
  };

  const getCurrentStepIndex = () => getStepIndex(currentStep);
  const currentStepIndex = getCurrentStepIndex();

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-2">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isCompleted = isStepCompleted(step.id);

        return (
          <div key={step.id} className="flex items-center justify-center">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : isCompleted
                    ? "bg-green-600 text-white"
                    : "bg-muted text-muted-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "w-8 h-0.5 mx-2",
                  currentStepIndex > index ? "bg-green-600" : "bg-muted"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderCurrentStep = () => {
    // Separate loading states to prevent input focus loss
    const customerStepLoading = createLoading; // Only creation loading affects customer step
    const servicesStepLoading = servicesStep.loading || createLoading;
    const schedulingStepLoading = schedulingStep.loading || createLoading;

    switch (currentStep) {
      case "customer":
        return (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">{t("booking.customerStep.title")}</h2>
              <p className="text-muted-foreground text-sm">{t("booking.customerStep.subtitle")}</p>
            </div>
            <MobileCustomerStep customerStep={customerStep} loading={customerStepLoading} />
          </div>
        );
      case "services":
        return (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">{t("booking.servicesStep.title")}</h2>
              <p className="text-muted-foreground text-sm">{t("booking.servicesStep.subtitle")}</p>
            </div>
            <MobileServicesStep servicesStep={servicesStep} loading={servicesStepLoading} />
          </div>
        );
      case "scheduling":
        return (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">{t("booking.schedulingStep.title")}</h2>
              <p className="text-muted-foreground text-sm">
                {t("booking.schedulingStep.subtitle")}
              </p>
            </div>
            <MobileSchedulingStep schedulingStep={schedulingStep} loading={schedulingStepLoading} />
          </div>
        );
      default:
        return null;
    }
  };

  const loading =
    customerStep.loading || servicesStep.loading || schedulingStep.loading || createLoading;
  const error = customerStep.error || servicesStep.error || schedulingStep.error || createError;

  // Reset all steps when dialog closes
  if (!open) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with Step Indicator */}
      <div className="px-4 py-4 border-b bg-card">{renderStepIndicator()}</div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mx-4 mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="flex-1 px-4 py-6 overflow-y-auto">{renderCurrentStep()}</div>

      {/* Footer Actions */}
      <div className="border-t bg-card p-4">
        <div className="flex justify-between items-center gap-3">
          <Button variant="outline" onClick={prevStep} disabled={isFirstStep()} className="flex-1">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("actions.back")}
          </Button>

          {!isLastStep() ? (
            <Button
              onClick={nextStep}
              disabled={
                (currentStep === "customer" && !customerStep.isValid()) ||
                (currentStep === "services" && !servicesStep.isValid())
              }
              className="flex-1"
            >
              {t("actions.next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!canSubmit()} className="flex-1">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("createAppointment")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
